import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import dayjs from "@/lib/bizday";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Users, AlertTriangle, History } from "lucide-react";
import { toast } from "sonner";

const t = (s) => dayjs(s).format("h:mm A");
const span = (c) => `${c.service_name || c.kind} · ${t(c.start_time)}–${t(c.end_time)}`;

export function StaffPlanButton({ booking, compact }) {
  const [open, setOpen] = useState(false);
  if (["cancelled", "no_show"].includes(booking.status)) return null;
  return (
    <>
      <button title="Staff plan" data-testid={`staff-plan-btn-${booking.id}`} onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className={`inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs font-medium text-violet-700 hover:bg-violet-50 ${compact ? "p-2" : ""}`}>
        <Users className="h-4 w-4" />{!compact && "Staff"}
      </button>
      {open && <StaffPlanDialog bookingId={booking.id} title={`${booking.customer_name || ""}${booking.pet_name ? ` · ${booking.pet_name}` : ""}`} onClose={() => setOpen(false)} />}
    </>
  );
}

export function StaffPlanDialog({ bookingId, title, onClose }) {
  const { data, isLoading } = useQuery({ queryKey: ["assignments", bookingId], queryFn: async () => (await api.get(`/bookings/${bookingId}/assignments`)).data });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="staff-plan-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">Staff plan</DialogTitle>
          <DialogDescription>{title} — assign staff and times for each service. Staff are busy only during their own service blocks.</DialogDescription>
        </DialogHeader>
        {isLoading ? <div className="py-6 text-sm text-slate-400">Loading…</div> : !data?.items?.length ? (
          <div className="py-6 text-sm text-slate-500" data-testid="staff-plan-empty">This booking has no services that need staff time (e.g. boarding stays).</div>
        ) : (
          <div className="space-y-3">
            {data.default_staff_name && <div className="text-xs text-slate-500" data-testid="staff-plan-default">Booking default: <b>{data.default_staff_name}</b></div>}
            {data.items.map((r) => <AssignmentRow key={r.service_index} bookingId={bookingId} row={r} canOverride={data.can_override} />)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function AssignmentRow({ bookingId, row, canOverride }) {
  const qc = useQueryClient();
  const [staffIds, setStaffIds] = useState(row.staff_ids || []);
  const [start, setStart] = useState(dayjs(row.start_time).format("YYYY-MM-DDTHH:mm"));
  const [dur, setDur] = useState(row.duration || dayjs(row.end_time).diff(dayjs(row.start_time), "minute"));
  const [notes, setNotes] = useState(row.notes || "");
  const [conflict, setConflict] = useState(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const startIso = dayjs(start).format("YYYY-MM-DDTHH:mm:ssZ");
  const endIso = dayjs(start).add(Number(dur) || 0, "minute").format("YYYY-MM-DDTHH:mm:ssZ");
  const idx = row.service_index;

  const save = async (override) => {
    setBusy(true);
    try {
      await api.put(`/bookings/${bookingId}/assignments/${idx}`, { staff_ids: staffIds, start_time: startIso, end_time: endIso, notes, override_reason: override ? reason : "" });
      setConflict(null); setReason("");
      toast.success(override ? "Saved with override (logged)" : "Assignment saved");
      ["assignments", "bookings", "cal", "staff-avail", "vet-assignment-issues", "vet-my-queue"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (e) {
      const d = e?.response?.data?.detail;
      if (e?.response?.status === 409 && d?.conflicts) setConflict(d); else toast.error(apiErr(e));
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 p-3" data-testid={`assignment-row-${idx}`}>
      <div className="flex items-center justify-between gap-2">
        <div className="font-medium text-slate-900">{row.service_name}</div>
        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold capitalize text-slate-600" data-testid={`assignment-status-${idx}`}>{(row.status || "").replace("_", " ")}</span>
      </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <div><Label className="text-xs">Start</Label><Input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} data-testid={`assignment-start-${idx}`} className="h-9" /></div>
        <div><Label className="text-xs">Duration (min)</Label><Input type="number" min={5} value={dur} onChange={(e) => setDur(e.target.value)} data-testid={`assignment-duration-${idx}`} className="h-9" /></div>
      </div>
      <StaffPicker startIso={startIso} endIso={endIso} exclude={row.id} value={staffIds} onChange={setStaffIds} idx={idx} row={row} />
      <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (optional)" className="mt-2 h-9" data-testid={`assignment-notes-${idx}`} />
      {!!row.conflicts?.length && !conflict && <div className="mt-2 rounded-md bg-amber-50 px-2 py-1.5 text-xs text-amber-800" data-testid={`assignment-existing-conflict-${idx}`}><AlertTriangle className="mr-1 inline h-3.5 w-3.5" />Overlaps: {row.conflicts.map(span).join("; ")}{row.override && ` — override by ${row.override.by_name}: “${row.override.reason}”`}</div>}
      {conflict && <ConflictBox conflict={conflict} canOverride={canOverride} reason={reason} setReason={setReason} busy={busy} onOverride={() => save(true)} idx={idx} />}
      <div className="mt-2 flex items-center justify-between">
        <HistoryList history={row.history} idx={idx} />
        <Button size="sm" disabled={busy} onClick={() => save(false)} data-testid={`assignment-save-${idx}`} className="bg-slate-900 text-white">{busy ? "Saving…" : "Save"}</Button>
      </div>
    </div>
  );
}

function StaffPicker({ startIso, endIso, exclude, value, onChange, idx, row }) {
  const { data: raw } = useQuery({ queryKey: ["staff-avail", startIso, endIso, exclude, row?.service_id], enabled: dayjs(endIso).isAfter(dayjs(startIso)),
    queryFn: async () => (await api.get(`/staff/availability?start=${encodeURIComponent(startIso)}&end=${encodeURIComponent(endIso)}&exclude_assignment=${exclude || ""}&service_id=${row?.service_id || ""}&category=${row?.category || ""}`)).data });
  // only staff eligible for this service (plus anyone already assigned, so legacy assignments stay visible)
  const data = raw && { ...raw, items: raw.items.filter((s) => s.eligible !== false || value.includes(s.id)) };
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="mt-2">
      <Label className="text-xs">Staff (select one or more)</Label>
      <div className="mt-1 max-h-40 space-y-1 overflow-y-auto rounded-md border border-slate-100 p-1.5" data-testid={`assignment-staff-list-${idx}`}>
        {data && !data.items.length && <p className="px-1.5 py-1 text-xs text-amber-700" data-testid={`assignment-staff-none-${idx}`}>No eligible staff for this service — it can stay unassigned.</p>}
        {(data?.items || []).map((s) => (
          <label key={s.id} className="flex cursor-pointer items-start gap-2 rounded px-1.5 py-1 text-xs hover:bg-slate-50" data-testid={`assignment-staff-${idx}-${s.id}`}>
            <input type="checkbox" checked={value.includes(s.id)} onChange={() => toggle(s.id)} className="mt-0.5" />
            <span className="min-w-0 flex-1"><span className="font-medium text-slate-800">{s.name}</span> <span className="text-slate-400">({s.role})</span>
              {s.available ? <span className="ml-1 text-emerald-600">free</span> : <span className={`ml-1 ${s.unavailable ? "text-rose-600" : "text-amber-600"}`}>{s.unavailable ? "unavailable" : "busy"}: {s.conflicts.map(span).join("; ")}</span>}
            </span>
          </label>
        ))}
      </div>
    </div>
  );
}

function ConflictBox({ conflict, canOverride, reason, setReason, busy, onOverride, idx }) {
  return (
    <div className="mt-2 rounded-md border border-amber-200 bg-amber-50 p-2 text-xs text-amber-900" data-testid={`assignment-conflict-${idx}`}>
      <div className="font-semibold"><AlertTriangle className="mr-1 inline h-3.5 w-3.5" />{conflict.code === "STAFF_UNAVAILABLE" ? "Staff unavailable at this time" : "Scheduling conflict"}</div>
      <ul className="mt-1 list-disc pl-4">{conflict.conflicts.map((c, i) => <li key={i}>{c.staff_name}: {span(c)}{c.booking_id ? ` (booking ${c.booking_id.slice(-6)})` : ""}</li>)}</ul>
      {canOverride ? (
        <div className="mt-2 flex gap-2">
          <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Override reason (required)" className="h-8 bg-white" data-testid={`assignment-override-reason-${idx}`} />
          <Button size="sm" disabled={busy || !reason.trim()} onClick={onOverride} data-testid={`assignment-override-btn-${idx}`} className="bg-amber-600 text-white hover:bg-amber-700">Override & save</Button>
        </div>
      ) : <div className="mt-1" data-testid={`assignment-override-denied-${idx}`}>Choose another time or staff member, or ask a manager to override.</div>}
    </div>
  );
}

function HistoryList({ history, idx }) {
  const [open, setOpen] = useState(false);
  if (!history?.length) return <span />;
  return (
    <div className="text-xs text-slate-500">
      <button onClick={() => setOpen(!open)} className="inline-flex items-center gap-1 hover:text-slate-800" data-testid={`assignment-history-toggle-${idx}`}><History className="h-3.5 w-3.5" /> History ({history.length})</button>
      {open && <ul className="mt-1 space-y-0.5" data-testid={`assignment-history-${idx}`}>{history.map((h, i) => <li key={i}>{dayjs(h.at).format("D MMM h:mm A")} · {h.by_name || "system"} · {h.action.replace(/_/g, " ")}{h.reason ? ` — “${h.reason}”` : ""}</li>)}</ul>}
    </div>
  );
}
